import { useQuery } from "@tanstack/react-query";
import { useRouteLoaderData } from "react-router";
import { getStamdataPerson } from "../../api/stamdata";
import { ROUTES } from "../../constants/routes";
import { HomeLoaderResponse } from "../../interfaces/loaders";
import { PersonStamdata } from "../../interfaces/stamdata";
import { getProfilStatus, ProfilStatus } from "../../lib/stamdataProfil";
import { stamdataQueryKeys } from "./stamdataQueryKeys";

export interface StamdataPersonResultat {
  /**
   * Medlemmets stamdata, eller `null` hvis der ikke er nogen at vise — profilen findes ikke, eller
   * forespørgslen er ikke hjemme endnu. Brug `profilStatus` til at skelne; det er derfor de to
   * returneres sammen.
   */
  stamdata: PersonStamdata | null;
  profilStatus: ProfilStatus;
}

/**
 * Medlemmets stamdata, læst friskt.
 *
 * Findes som hook, fordi query-optionsene nedenfor er dyrt lært og skal være ens overalt: to sider
 * læste før dette den samme stamdata på to måder, og den ene læste fra loader-data, som ikke
 * genkøres ved klientnavigation. Det gav to fejl af samme slags — en aktiv anmodning, der ikke låste
 * ID-kort-formularen (ADV-204), og en "profilen er ikke klar"-besked, der blev stående, efter at
 * profilen var blevet klar (ADV-288).
 *
 * Loader-data bruges stadig som `initialData`, så det første render er udfyldt. Det er et
 * øjebliksbillede fra sidste hårde sideindlæsning, ikke en kilde til sandhed — forespørgslen
 * overtrumfer det, så snart den er hjemme.
 */
export const useStamdataPerson = (): StamdataPersonResultat => {
  const loaderdata = useRouteLoaderData(ROUTES.homeIndex.id) as
    HomeLoaderResponse | undefined;
  const personId = loaderdata?.userInfo?.id;

  const { data } = useQuery({
    queryKey: stamdataQueryKeys.person(personId),
    queryFn: () => getStamdataPerson(personId!),
    enabled: Boolean(personId),
    initialData: loaderdata?.stamdata ?? undefined,
    // Begge overrides er nødvendige, fordi query-clienten globalt kører staleTime: Infinity. Uden
    // dem regnes initialData som frisk for evigt: så snart cache-rækken er ryddet (gcTime, 5 min),
    // gen-seedes forespørgslen fra det gamle loader-øjebliksbillede og hentes aldrig igen — og så
    // står medlemmet igen med en ulåst, tom formular trods en aktiv anmodning. Det er præcis den
    // fejl, ADV-204 findes for at lukke.
    staleTime: 0,
    refetchOnMount: "always",
  });

  return {
    stamdata: data ?? null,
    // Den rå `data` sendes videre, ikke den normaliserede: getProfilStatus skal kunne skelne
    // `undefined` (forespørgslen kører) fra `null` (svarede uden profil).
    profilStatus: getProfilStatus({
      stamdata: data,
      profilIkkeKlarFraLoader: loaderdata?.profilIkkeKlar,
    }),
  };
};
